import * as THREE from 'three';
import threeTheme from '../Utils/theme.js';

const shaderCache = new Map();

function loadShader(url) {
    if (!shaderCache.has(url)) {
        const request = fetch(url)
            .then((res) => res.text())
            .catch((error) => {
                shaderCache.delete(url);
                throw error;
            });

        shaderCache.set(url, request);
    }

    return shaderCache.get(url);
}

export default async function VignetteFX(experience, options = {}) {
    const {
        u_opaqueness = 0.3,
        u_sharpness = 0.77,
        u_circleSwell = 1.1,
        u_baseAlpha = 1.0,
    } = options;

    const u_color = threeTheme.vignette;

    experience.vignetteModifiers = {
        sharpnessInitial: u_sharpness,
        opaquenessInitial: u_opaqueness,
        circleSwellInitial: u_circleSwell,
        baseAlphaInitial: u_baseAlpha,
    };
    experience.vignetteMesh = null;

    const resizer = experience.resizer;
    const camera = experience.camera;
    const threejsUnit = 1;
    const aspectRatio16To9 = 16 / 9;
    const mediumRenderorder = 500; //get this from config later

    // SHADER
    const vertexShader = await loadShader('/shaders/vignetteVert.glsl');
    const fragmentShader = await loadShader('/shaders/vignetteFrag.glsl');
    const material = new THREE.ShaderMaterial({
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        uniforms: {
            u_sharpness: { value: u_sharpness },
            u_opaqueness: { value: u_opaqueness },
            u_circleSwell: { value: u_circleSwell },
            u_baseAlpha: { value: u_baseAlpha },
            u_color: { value: u_color },

        },
        transparent: true,
        depthWrite: false,
        depthTest: false,
    });

    // const geometry = new THREE.PlaneGeometry(threejsUnit, threejsUnit / aspectRatio16To9);
    const geometry = new THREE.PlaneGeometry(threejsUnit, threejsUnit);
    const mesh = new THREE.Mesh(geometry, material);
    camera.instance.add(mesh);
    mesh.position.set(0, 0, -threejsUnit / 2)
    mesh.frustumCulled = false;
    mesh.renderOrder = mediumRenderorder;
    experience.vignetteMesh = mesh;
    // Expose all four uniforms as live-editable properties
    for (const [prop, uniform] of [
        ['sharpness', 'u_sharpness'],
        ['opaqueness', 'u_opaqueness'],
        ['circleSwell', 'u_circleSwell'],
        ['baseAlpha', 'u_baseAlpha'],
    ]) {
        Object.defineProperty(experience.vignetteModifiers, prop, {
            get: () => material.uniforms[uniform].value,
            set: value => { material.uniforms[uniform].value = value; },
        });
    }

    // Scale the vignette plane to exactly fill the camera's visible area.
    // For perspective: derive from FOV + distance.
    // For orthographic: derive from frustum bounds.
    function scaleToViewport() {
        const cam = camera.instance;
        const d = Math.abs(mesh.position.z);

        if (cam.isOrthographicCamera) {
            mesh.scale.x = cam.right - cam.left;
            mesh.scale.y = cam.top - cam.bottom;
        } else {
            const vFov = cam.fov * Math.PI / 180;
            const h = 2 * d * Math.tan(vFov / 2);
            mesh.scale.y = h;
            mesh.scale.x = h * cam.aspect;
        }
    }

    // Expose so Camera can re-trigger after type switch
    experience.vignetteModifiers.rescale = scaleToViewport;

    scaleToViewport();

    experience.eventBus.on('resize', () => {
        scaleToViewport();
    });
}
